import { action, computed, makeObservable, observable } from 'mobx';
import { BindingProps, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { InputState, input } from '../input';
import { SyntheticEvent } from 'react';
import Routes from '../../routes';
import { JobModel, Team } from '../../entities';
import { isNonEmptyString } from '../../core';
import { MomentResultItem } from '../../pages/searchResultsPage/momentResultsCatalog';

export type DashboardSearchMode = 'idle' | 'ask' | 'search';

type Props = { searchMode?: DashboardSearchMode | null } & 
  BindingProps<{
    teamId?: string | null
  }>

export class SearchBarState
  extends StoreNode {
  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable(this, {
      searchMode: observable,
      isFocused: observable,
      teamId: computed,
      showSuggestions: computed,
      jobSuggestions: computed,
      topicSuggestions: computed,
      momentSuggestions: computed,
      isStandardSearchMode: computed,
      team: computed,
      searchModel: computed,
      cliprGptEnabled: computed,
      showOptions: computed,
      handleFocus: action,
      handleSubmit: action,
      handleClear: action,
      reset: action,
      setSearchMode: action,
      handleSearchClick: action,
      handleAskClick: action,
    });

    this.inputModel = input(this, {
      name: 'searchOptionsInput',
      onFocus: this.handleFocus,
    });

    this.searchMode = props?.searchMode ?? 'idle';
  }

  readonly inputModel: InputState;
  searchMode: DashboardSearchMode;
  isFocused: boolean = false;

  get teamId(): string | null {
    return this.resolvedProps.teamId ?? null;
  }

  get showSuggestions(): boolean {
    return this.store.searchService.showSuggestions;
  }

  get jobSuggestions(): JobModel[] {
    return this.store.searchService.jobSuggestions;
  }

  get topicSuggestions(): MomentResultItem[] {
    return this.store.searchService.topicSuggestions;
  }

  get momentSuggestions(): MomentResultItem[] {
    return this.store.searchService.momentSuggestions;
  }

  get isStandardSearchMode(): boolean {
    return this.searchMode === 'search' || !this.cliprGptEnabled;
  }

  get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  get searchModel(): InputState {
    return this.store.searchService.getSearchModel();
  }

  get cliprGptEnabled(): boolean {
    return this.store.aiService.getCliprGptEnabled(this.team);
  }

  get showOptions(): boolean {
    return !!this.inputModel.value && this.isFocused;
  }

  handleFocus = () => {
    this.isFocused = true;
  }

  handleSubmit = (e: SyntheticEvent, replaceOnSubmit: boolean = false) => {
    e.preventDefault();
    
    if (this.isStandardSearchMode) {
      this.store.searchService.submitToSearchPage(replaceOnSubmit, this.teamId);
      return;
    }
    
    this.store.searchService.submitToGptPage(replaceOnSubmit, this.teamId);
  };

  handleClear = (e: SyntheticEvent, replaceOnSubmit: boolean = false) => {
    e.preventDefault();

    if (this.isSearchPage()) {
      this.handleSubmit(e, replaceOnSubmit);
      return;
    }

    if (this.isStandardSearchMode) {
      this.reset();
      return;
    }
  };

  reset = () => {
    if (this.isSearchPage()) {
      return;
    }

    this.inputModel.clear();
    this.searchMode = 'idle';
  }

  private isSearchPage() {
    const { pathname } = window.location;
    return pathname === Routes.search || pathname === Routes.librarySearchWidget();
  }

  setSearchMode = (searchMode: DashboardSearchMode) => {
    this.searchMode = searchMode;
  }

  handleSearchClick = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    this.searchMode = 'search';
    this.store.searchService.loadValueToSearchPage(this.inputModel.value);

    // TODO: replace with React ref-based focus management
    // Consecutive focus events after search option selection are too fast; setTimeout(0) defers to next tick.
    setTimeout(() => {
      const component: HTMLElement | null = document.querySelector('[data-focus-id=search]');
      component?.focus();
    }, 0);
  }

  handleAskClick = (evt: React.MouseEvent, value?: string | null) => {
    evt.stopPropagation();
    const submittedValue = value ?? this.inputModel.value;

    if (!submittedValue || !isNonEmptyString(submittedValue)) {
      return;
    }
    
    this.store.searchService.submitToGptPage(false, this.teamId, submittedValue);
  }
}
